import React, { useCallback, useState, useRef, ReactNode } from "react";
import PropTypes from "prop-types";
import { Button, TextField } from "react-md";
import MDialog, { MDialogProps } from "components/Shared/MDialog";

export const DialogContext = React.createContext(null);

export interface DialogProviderAction {
  label: ReactNode;
  isPrimary?: boolean;
  onClick?: () => boolean | void;
};

export interface DialogPromptProps {
  title: ReactNode;
  message?: ReactNode;
  onOkClicked: (inputValue: string) => boolean | void;
  onCancelClicked?: (inputValue: string) => boolean | void;
  okLabel?: string;
  cancelLabel?: string;
  props?: Partial<MDialogProps>;
  inputPlaceholder?: string;
};

export interface DialogProviderValue {
  show: (title: ReactNode, children: ReactNode, actions?: DialogProviderAction[], props?: Partial<MDialogProps>) => void;
  /** Shows a dialog with yes and no options. Returning false from a click handler will not hide the dialog. Use that if you want to show another dialog in the click handler  */
  showYesNo: (title: ReactNode, message: ReactNode, onYesClick: () => boolean | void, onNoClick?: () => boolean | void, yesLabel?: any, noLabel?: any, props?: Partial<MDialogProps>) => void;
  /** Shows a dialog with an ok button. Returning false from a click handler will not hide the dialog. Use that if you want to show another dialog in the click handler  */
  showOk: (title: any, message: any, onOkClick?: any, okLabel?: any, props?: Partial<MDialogProps>) => void;
  hide: () => void;
  prompt: (props: DialogPromptProps) => void;
  isOpen?: boolean;
};

const DialogProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogChildren, setDialogChildren] = useState<ReactNode>();
  const [props, setProps] = useState<Partial<MDialogProps>>();

  const clearPropsTimeout = useRef<number>();

  const clearClearPropsTimeout = () => {
    if (clearPropsTimeout.current) {
      window.clearTimeout(clearPropsTimeout.current);
      clearPropsTimeout.current = null;
    }
  };

  /*
    props are sent to the MDialog control, some important ones include className/width
    actions are provided as
    [{ label, isPrimary, onClick }]
  */
  const show = useCallback((title: ReactNode, children: ReactNode, actions: DialogProviderAction[], props?: Partial<MDialogProps>) => {
    setDialogChildren(children);
    setIsOpen(true);
    clearClearPropsTimeout();
    props = { ...props };
    if (actions) {
      props.contentActions = actions.map((a, i) => 
        <Button
          key={`button-${i}`}
          primary={a.isPrimary}
          raised
          onClick={() => {
            let shouldHide = true;
            if (a.onClick) {
              if (a.onClick() === false) shouldHide = false;
            }
            if (shouldHide) hide();
          }}>
            {a.label}
          </Button>);
    }
    if (title) props.title = title;
    setProps(props || {});
  }, []);

  const showYesNo = useCallback((title: ReactNode, message: ReactNode, onYesClick: () => boolean | void, onNoClick: () => boolean | void, yesLabel = "Yes", noLabel = "No", props?: Partial<MDialogProps>) =>
    show(title, message, [
      { label: yesLabel, isPrimary: true, onClick: onYesClick },
      { label: noLabel, isPrimary: true, onClick: onNoClick },
    ], props), []);

  const showOk = useCallback((title, message, onOkClick, okLabel = "Ok", props?: Partial<MDialogProps>) =>
    show(title, message, [{ label: okLabel, isPrimary: true, onClick: onOkClick }], props), []);

  const hide = useCallback(() => {
    setIsOpen(false);
    setDialogChildren(null);

    clearClearPropsTimeout(); // should never need this here but it cant hurt
    clearPropsTimeout.current = window.setTimeout(() => {
      setProps(null);
      clearPropsTimeout.current = null;
    }, 1200); // if we clear the props immediately the dialog loses styling while its animating away
  }, []);

  const prompt = useCallback(({
    title,
    message,
    onOkClicked,
    onCancelClicked,
    okLabel = "Ok",
    cancelLabel = "Cancel",
    inputPlaceholder = null,
    props,
  }: DialogPromptProps) => {
    let inputValue = { current: "" };
    
    const promptContent = (
      <div>
        {message && <div className="prompt-message">{message}</div>}
        <TextField
          onChange={s => inputValue.current = s as string}
          placeholder={inputPlaceholder}
        />
      </div>
    );

    show(title, promptContent, [
      { label: okLabel, isPrimary: true, onClick: () => onOkClicked(inputValue.current) },
      { label: cancelLabel, onClick: () => onCancelClicked?.(inputValue.current) },
    ], props);
  }, []);

  const providerVal = useRef<DialogProviderValue>({
    show,
    showYesNo,
    showOk,
    hide,
    prompt,
  });

  // leave this outside of the useRef call otherwise it will never get properly updated
  providerVal.current.isOpen = isOpen;
  
  return (
    <DialogContext.Provider value={providerVal.current}>
      {children}
      <MDialog
        id="global-mueller-dialog"
        visible={isOpen}
        dontSpecifyWidth={props?.width === undefined} // if someone specifies width then we need dontSpecifyWidth = false so the width can matter
        {...props}
      >
        {dialogChildren}
      </MDialog>
    </DialogContext.Provider>
  );
};

DialogProvider.propTypes = {
  children: PropTypes.node,
};

export default DialogProvider;
